import * as Yup from "yup";
import {
  extractIndianNationalFromPhoneInput,
  hasDuplicateIndiaCodeInPhoneInput,
  isBlankIndiaPhone,
  isIndiaCountryCode,
} from "../../../utils/newOnboarding/validations";


export const emailSchema = Yup.object({
  email: Yup.string()
    .required("Email cannot be empty")
    .test("email-validation", "Enter a valid email", function (value) {
      if (!value) return true; // Let required validation handle empty values

      // Check if email starts with a number
      if (!isNaN(Number(value[0]))) {
        return this.createError({
          message: "Email cannot start with a number",
        });
      }

      // Only validate email format if user has started typing an email (contains @)
      if (value.includes("@")) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return this.createError({
            message: "Enter a valid email",
          });
        }
      }

      return true;
    }),
  agreeToTerms: Yup.boolean().oneOf([true], "Please accept terms"),
});



export const otpSchema = Yup.object({
  verificationCode: Yup.string().length(6, "Code must be 6 digits").required(),
});

export const personalSchema = (countryCode: string) =>
  Yup.object({
    name: Yup.string()
      .trim()
      .required("Name is required")
      .max(40, "Maximum 40 chars allowed")
      .test(
        "no-at-symbol",
        "The field should not contain the special character",
        (value) => !value?.includes("@")
      )
      .matches(
        /^[a-zA-Z0-9 ]*$/,
        "The field should not contain special characters"
      )
      .test("not-only-numbers", "Name cannot be only numbers", (value) => {
        if (!value) return true; // Let required validation handle empty values
        const trimmedValue = value.trim();
        return !/^\d+$/.test(trimmedValue); // Check if it's not only digits
      }),

    businessName: Yup.string()
      .trim()
      .required("Business name is required")
      .min(3, "Minimum 3 chars allowed")
      .max(50, "Maximum 50 chars allowed")
      .test(
        "no-at-symbol",
        'The field should not contain the special character "@"',
        (value) => !value?.includes("@")
      )
      .matches(
        /^[a-zA-Z0-9 ]*$/,
        "The field should not contain special characters"
      ),

    countryCode: Yup.string(),

    phone: Yup.string().test(
      "phone-validation",
      "Phone validation failed",
      function (value) {
        const country =
          this.parent.countryCode || countryCode || "IN";
        if (!isIndiaCountryCode(country)) {
          return true;
        }

        if (isBlankIndiaPhone(value)) {
          return this.createError({ message: "Phone number is required" });
        }

        const national = extractIndianNationalFromPhoneInput(value);
        if (!national) {
          if (hasDuplicateIndiaCodeInPhoneInput(value)) {
            return this.createError({
              message:
                "Do not include country code (+91); enter a 10-digit mobile number",
            });
          }
          const digitsOnly = String(value ?? "").replace(/\D/g, "");
          if (digitsOnly.startsWith("91") && digitsOnly.length < 12) {
            return this.createError({
              message: "Phone number must be 10 digits",
            });
          }
          return this.createError({
            message: "Phone number must start with 6, 7, 8, or 9",
          });
        }

        return true;
      }
    ),

    category: Yup.object()
      .shape({
        category_pid: Yup.string().trim().required("Please select one category"),
        category: Yup.string().trim().required("Please select one category"),
      })
      .typeError("Please select one category")
      .required("Please select one category"),

    password: Yup.string()
      .required("Password is required")
      .min(8, "Characters must be between 8 and 15")
      .max(15, "Characters must be between 8 and 15")
      .matches(/[a-z]/, "Must include a lowercase letter")
      .matches(/[A-Z]/, "Must include an uppercase letter")
      .matches(/\d/, "Must include a digit")
      .matches(/[^A-Za-z0-9\s]/, "Must include a special character")
      .matches(/^\S+$/, "Spaces are not allowed"),

    confirmPassword: Yup.string()
      .required("Confirm your password")
      .oneOf([Yup.ref("password")], "Password must match")
      .test("password-match", "Password must match", function (value) {
        if (!value) return true; // Let required validation handle empty values
        return value === this.parent.password;
      }),
  });
  